import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, ImageIcon, Loader2, Trash2, Upload } from "lucide-react";

import {
    BACKGROUND_IMAGE_ACCEPT,
    MAX_BACKGROUND_IMAGE_BYTES,
    MAX_CENTER_BACKGROUNDS,
    MAX_OWN_BACKGROUNDS,
    type MeetingBackground,
} from "../../domain/meetingBackground";
import {
    meetingBackgroundImageUrl,
    useAddMeetingBackground,
    useCenterMeetingBackgrounds,
    useDeleteMeetingBackground,
    useMeetingBackgrounds,
} from "../../application/useMeetingBackgrounds";

type Props = {
    /** The therapist's own shelf, or their centre's shared one. */
    scope: "own" | "center";
    /**
     * Whether they may add to and remove from this shelf. Their own always;
     * the centre's only for one of its administrators — the same distinction
     * the meditation track library draws.
     */
    canManage?: boolean;
    /**
     * Where it is drawn. The room's menu is a narrow dark panel and the centre
     * panel is a page; everything else about the shelf is the same, which is
     * the whole reason there is one component rather than two.
     */
    variant?: "room" | "page";
    /** Set when the shelf is a picker: which picture is in use. */
    selectedId?: string | null;
    /** Set when the shelf is a picker: choosing one. */
    onSelect?: (background: MeetingBackground) => void;
    /** Off where the caller draws its own heading. */
    heading?: boolean;
};

/**
 * The pictures on a shelf, and what can be done with them.
 *
 * One component in two places, because they are the same shelf: the picker
 * inside a call, where a therapist chooses what is behind them, and the
 * centre's tools page, where an administrator fills the library for everybody.
 * An administrator should not have to join a call to put a wall in the centre's
 * library, and a therapist should not find a different form in the room from
 * the one they learned on the page.
 *
 * The bytes come through our API rather than from the bucket — a centre's shelf
 * has to answer to that centre's members and to nobody else, which no bucket
 * can decide — and every blob URL made here is revoked when the tile goes.
 */
export default function MeetingBackgroundShelf({
    scope,
    canManage = true,
    variant = "page",
    selectedId = null,
    onSelect,
    heading = true,
}: Props) {
    const { t } = useTranslation();
    const own = useMeetingBackgrounds();
    const center = useCenterMeetingBackgrounds();
    const { data, isLoading } = scope === "center" ? center : own;
    const backgrounds = data ?? [];

    const addBackground = useAddMeetingBackground();
    const deleteBackground = useDeleteMeetingBackground();
    const fileRef = useRef<HTMLInputElement>(null);
    const [error, setError] = useState<string | null>(null);

    const limit = scope === "center" ? MAX_CENTER_BACKGROUNDS : MAX_OWN_BACKGROUNDS;
    const full = backgrounds.length >= limit;
    const room = variant === "room";

    const add = async (file: File) => {
        setError(null);
        // Said before the upload rather than after it: the API weighs the
        // stored object, so without this a therapist waits for thirty
        // megabytes to travel only to be told it was never going to be kept.
        if (file.size > MAX_BACKGROUND_IMAGE_BYTES) {
            setError(t("meetingBackground.tooLarge"));
            return;
        }
        try {
            await addBackground.mutateAsync({
                file,
                // The file's own name, without its extension. A therapist can
                // see which picture is which from the thumbnail; the name is
                // there for the ones who cannot.
                name: file.name.replace(/\.[^.]+$/, "").slice(0, 80) || file.name,
                forCenter: scope === "center",
            });
        } catch {
            setError(t("meetingBackground.uploadFailed"));
        }
    };

    const remove = async (background: MeetingBackground) => {
        setError(null);
        try {
            await deleteBackground.mutateAsync(background.id);
        } catch {
            setError(t("meetingBackground.removeFailed"));
        }
    };

    return (
        <div className={room ? "flex flex-col gap-1.5" : "flex flex-col gap-3"}>
            {heading && (
                <p
                    className={
                        room
                            ? "px-1 text-xs font-medium uppercase tracking-wide text-room-ink-muted"
                            : "text-sm font-medium text-content-primary"
                    }
                >
                    {t(
                        scope === "center"
                            ? "meetingBackground.centerShelf"
                            : "meetingBackground.ownShelf"
                    )}
                </p>
            )}

            {isLoading ? (
                <p
                    className={`flex items-center gap-2 px-1 text-xs ${
                        room ? "text-room-ink-muted" : "text-content-secondary"
                    }`}
                >
                    <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                    {t("common.loading")}
                </p>
            ) : backgrounds.length === 0 ? (
                <p
                    className={`px-1 text-xs ${
                        room ? "text-room-ink-muted" : "text-content-secondary"
                    }`}
                >
                    {t(
                        scope === "center"
                            ? "meetingBackground.centerEmpty"
                            : "meetingBackground.empty"
                    )}
                </p>
            ) : (
                <ul
                    className={
                        room
                            ? "grid grid-cols-3 gap-1.5"
                            : "grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4"
                    }
                    data-testid={`background-shelf-${scope}`}
                >
                    {backgrounds.map((background) => (
                        <li key={background.id} className="relative min-w-0">
                            <BackgroundTile
                                background={background}
                                room={room}
                                selected={selectedId === background.id}
                                onSelect={onSelect}
                            />
                            {canManage && background.can_manage !== false && (
                                <button
                                    type="button"
                                    onClick={() => void remove(background)}
                                    disabled={deleteBackground.isPending}
                                    aria-label={t("meetingBackground.remove", {
                                        name: background.name,
                                    })}
                                    title={t("meetingBackground.remove", {
                                        name: background.name,
                                    })}
                                    className="absolute right-1 top-1 flex size-6 items-center justify-center rounded-md bg-black/60 text-white opacity-90 hover:bg-black/80 disabled:opacity-50"
                                >
                                    <Trash2 size={12} aria-hidden="true" />
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {canManage && (
                <>
                    <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        disabled={full || addBackground.isPending}
                        data-testid={`background-upload-${scope}`}
                        className={
                            room
                                ? "room-menu-item disabled:opacity-50"
                                : "inline-flex w-fit items-center gap-2 rounded-xl border border-border-subtle px-3 py-2 text-sm text-content-primary hover:bg-surface-sunken disabled:opacity-50"
                        }
                    >
                        {addBackground.isPending ? (
                            <Loader2 size={14} className="animate-spin" aria-hidden="true" />
                        ) : (
                            <Upload size={14} aria-hidden="true" />
                        )}
                        <span>
                            {t(
                                scope === "center"
                                    ? "meetingBackground.uploadToCenter"
                                    : "meetingBackground.upload"
                            )}
                        </span>
                    </button>

                    <input
                        ref={fileRef}
                        type="file"
                        accept={BACKGROUND_IMAGE_ACCEPT}
                        className="sr-only"
                        data-testid={`background-file-${scope}`}
                        onChange={(e) => {
                            const file = e.target.files?.[0];
                            // Cleared so that picking the same file again after
                            // a failure still fires a change event.
                            e.target.value = "";
                            if (file) void add(file);
                        }}
                    />

                    {full && (
                        <p
                            className={`px-1 text-xs ${
                                room ? "text-room-ink-muted" : "text-content-secondary"
                            }`}
                        >
                            {t("meetingBackground.full", { count: limit })}
                        </p>
                    )}
                </>
            )}

            {error && (
                <p
                    role="alert"
                    className={`px-1 text-xs font-medium ${
                        room ? "text-room-live" : "text-status-critical"
                    }`}
                >
                    {error}
                </p>
            )}
        </div>
    );
}

/**
 * One picture, drawn from bytes this component fetched itself.
 *
 * Each tile owns its blob URL and revokes it when it goes: the images are not
 * public, so there is no `src` a browser could simply be pointed at, and a URL
 * nobody revokes pins its bytes for the lifetime of the page.
 */
function BackgroundTile({
    background,
    room,
    selected,
    onSelect,
}: {
    background: MeetingBackground;
    room: boolean;
    selected: boolean;
    onSelect?: (background: MeetingBackground) => void;
}) {
    const { t } = useTranslation();
    const [src, setSrc] = useState<string | null>(null);

    useEffect(() => {
        let url: string | null = null;
        let cancelled = false;
        void meetingBackgroundImageUrl(background.id)
            .then((created) => {
                if (cancelled) {
                    URL.revokeObjectURL(created);
                    return;
                }
                url = created;
                setSrc(created);
            })
            // A thumbnail that will not load is a tile without a picture, not
            // an error worth interrupting anybody for.
            .catch(() => undefined);
        return () => {
            cancelled = true;
            if (url) URL.revokeObjectURL(url);
        };
    }, [background.id]);

    const picture = (
        <>
            {src ? (
                <img
                    src={src}
                    alt=""
                    className="h-full w-full rounded-lg object-cover"
                />
            ) : (
                <span className="flex h-full w-full items-center justify-center rounded-lg">
                    <ImageIcon size={16} aria-hidden="true" className="opacity-50" />
                </span>
            )}
            {selected && (
                <span className="absolute left-1 top-1 flex size-5 items-center justify-center rounded-md bg-black/60 text-white">
                    <Check size={12} aria-hidden="true" />
                </span>
            )}
        </>
    );

    const frame = `relative block aspect-video w-full overflow-hidden rounded-lg border ${
        room ? "border-room-line bg-room-hover" : "border-border-subtle bg-surface-sunken"
    } ${selected ? "ring-2 ring-[color:var(--color-accent)]" : ""}`;

    if (!onSelect) {
        return (
            <figure className="min-w-0">
                <span className={frame}>{picture}</span>
                <figcaption
                    className={`mt-1 block truncate text-xs ${
                        room ? "text-room-ink" : "text-content-secondary"
                    }`}
                >
                    {background.name}
                </figcaption>
            </figure>
        );
    }

    return (
        <>
            <button
                type="button"
                onClick={() => onSelect(background)}
                aria-pressed={selected}
                aria-label={t("meetingBackground.use", { name: background.name })}
                title={background.name}
                className={frame}
            >
                {picture}
            </button>
            <span className="mt-1 block truncate text-xs text-room-ink">
                {background.name}
            </span>
        </>
    );
}
